import appSettings from 'wanderu-config';
import {
    selectLocale,
    selectCurrency,
} from 'wanderu-i18n';
import { getLogger } from 'wanderu-logger';
import { AccountService } from 'wanderu-service-account';
import { identifyUser, SnowplowService } from 'wanderu-service-analytics';

import { actions as userInfoActions } from '../../appstate/userInfo';
import { actions } from './base';

const logger = getLogger('wanderu-duck-account');

async function cognitoLogInHelper(email, password) {
    try {
        return await AccountService.logInWithCognito(email, password);
    } catch (ex) {
        const domainUserInfo = appSettings.env.isClient
            ? SnowplowService.getDomainUserInfo()
            : {};

        logger.error('cognitoLogIn', ex, {
            snowplowIds: {
                networkUserId: SnowplowService.getNetworkUserId(),
                ...domainUserInfo,
            },
        });
        throw ex;
    }
}

async function facebookLogInHelper() {
    try {
        return await AccountService.logInWithFacebook();
    } catch (ex) {
        const domainUserInfo = appSettings.env.isClient
            ? SnowplowService.getDomainUserInfo()
            : {};

        logger.error('facebookLogin', ex, {
            snowplowIds: {
                networkUserId: SnowplowService.getNetworkUserId(),
                ...domainUserInfo,
            },
        });
        throw ex;
    }
}

async function googleButtonLogInHelper(response) {
    try {
        return await AccountService.logInWithGoogleButton(response);
    } catch (ex) {
        const domainUserInfo = appSettings.env.isClient
            ? SnowplowService.getDomainUserInfo()
            : {};

        logger.error('googleButtonLogIn', ex, {
            snowplowIds: {
                networkUserId: SnowplowService.getNetworkUserId(),
                ...domainUserInfo,
            },
        });
        throw ex;
    }
}

async function autoLogInHelper() {
    return AccountService.performAutoLogin();
}

function cognitoLogIn(email, password) {
    return async (dispatch) => {
        const cognitoLogInFn = cognitoLogInHelper.bind(null, email, password);
        return dispatch(logIn(cognitoLogInFn));
    };
}

function facebookLogIn() {
    return async (dispatch) => {
        return dispatch(logIn(facebookLogInHelper));
    };
}

function googleButtonLogIn(response) {
    return async (dispatch) => {
        const googleLogInFn = googleButtonLogInHelper.bind(null, response);
        return dispatch(logIn(googleLogInFn));
    };
}

function autoLogIn() {
    return async (dispatch) => {
        return dispatch(logIn(autoLogInHelper));
    };
}

function logIn(logInFn) {
    return async (dispatch, getState) => {
        dispatch(actions.startWork());

        let meta;
        let error;
        try {
            // For google and facebook there is no sign up. The first log-in will sign them up
            const {
                userInfo,
                loyaltyInfo,
                identityId,
                newAccount,
            } = await logInFn();

            const { provider } = await AccountService.getLoggedInState();
            const result = {
                identityId,
                loyaltyInfo,
                provider,
                userInfo,
            };

            dispatch(userInfoActions.setLoggedIn(result));

            const locale = selectLocale(getState());
            const currency = selectCurrency(getState());
            identifyUser({
                currency,
                email: userInfo.email,
                firstName: userInfo.firstName,
                identityId,
                lastName: userInfo.lastName,
                locale,
                source: 'account_login',
            });

            if (newAccount) {
                dispatch(userInfoActions.markAsNewAccount());
            }
        } catch (ex) {
            dispatch(userInfoActions.setLoggedOut());

            error = {
                code: ex.code,
                message: ex.message,
                name: ex.name,
            };
        } finally {
            dispatch(actions.endWork(meta, error));
        }
    };
}

function setWorking() {
    return actions.startWork();
}

function reset() {
    return actions.reset();
}

export {
    autoLogIn,
    cognitoLogIn,
    facebookLogIn,
    googleButtonLogIn,
    reset,
    setWorking,
};
